import { useState, useEffect, useMemo } from "react";
import { Listbox, ListboxItem } from "@heroui/listbox";
import { Chip } from "@heroui/chip";
import { ScrollShadow } from "@heroui/scroll-shadow";
import { Avatar } from "@heroui/avatar";
import { Input } from "@heroui/input";
import { Skeleton } from "@heroui/skeleton";
import { useSession, useUser } from "@clerk/clerk-react";
import { CirclePlus } from "lucide-react";
import { Button } from "@heroui/button";
import { getMyConnections as getUserMembersApi } from "@/api/users";
import CreateMemberDrawer from "../drawers/create-member-drawer";

const InviteMembersComp = ({ onChange, currentMembers = [] }) => {
const { session } = useSession();
const { user } = useUser();

const [allUsers, setAllUsers] = useState([]);
const [values, setValues] = useState(new Set(currentMembers));
const [search, setSearch] = useState("");
const [loading, setLoading] = useState(true);
const [showCreate, setShowCreate] = useState(false)

useEffect(() => {
  getMembers();
}, []);

async function getMembers() {
  setLoading(true);
  const token = await session.getToken();
  getUserMembersApi(token).then((_resp) => {
    if (_resp.data.Result === "OK") {
      let records = _resp.data.Records || [];
      records = records.filter((rec) => String(rec.id) !== String(user?.id));
      setAllUsers(records);
    } else {
      setAllUsers([]);
    }
  }).catch((_resp) => {
    setAllUsers([]);
  }).finally(() => {
    setLoading(false);
  })
}

const arrayValues = useMemo(() => Array.from(values), [values]);

const topContent = useMemo(() => {
  if (loading) return null;
  if (!arrayValues.length) return null;

  return (
    <ScrollShadow hideScrollBar className="w-full flex py-0.5" orientation="horizontal">
      {arrayValues.map((value) => {
        const found = allUsers.find((u) => String(u.id) === String(value));
        return (
          <Chip key={value} className="me-1">
            <span className="capitalize">{found?.fullname}</span>
          </Chip>
        );
      })}
    </ScrollShadow>
  );
}, [arrayValues, allUsers, loading]);

const filteredUsers = useMemo(() => {
  if (loading) return [];
  if (!search.trim()) return allUsers;
  const q = search.toLowerCase();
  return allUsers.filter(
    (u) =>
      u.fullname?.toLowerCase().includes(q) ||
      u.email_address?.toLowerCase().includes(q)
  );
}, [allUsers, search, loading]);

const handleSelectionChange = (newValues) => {
  const newSet = new Set(newValues);
  const oldSet = values;

  for (let id of newSet) {
    if (!oldSet.has(id)) {
      const part = allUsers.find((item) => String(item.id) === String(id));
      onChange?.("add", part);
    }
  }

  for (let id of oldSet) {
    if (!newSet.has(id)) {
      const part = allUsers.find((item) => String(item.id) === String(id));
      onChange?.("remove", part);
    }
  }

  setValues(newSet);
};

function onCreateMemberDrawerResp(_cmd, _value){
  if(_cmd === 'close'){
    setShowCreate(false)
    getMembers()
  }
}

return (
  <div className="w-full flex flex-col gap-2">
    <Input onChange={(e) => setSearch(e.target.value)} value={search} placeholder="Search people..." className="flex-1"
      classNames={{
        inputWrapper:
          "bg-white border border-neutral-200 hover:bg-neutral-50 shadow-sm rounded-xl " +
          "dark:bg-content1/70 dark:border-default-200/60 dark:backdrop-blur-xl",
        input: "text-neutral-900 placeholder:text-neutral-400 dark:text-foreground",
      }}
      endContent={
        <button onClick={() => {setShowCreate(true)}} type="button" className="text-neutral-500 hover:text-neutral-700 dark:text-foreground">
          <CirclePlus className="w-5 h-5" />
        </button>
      }
    />

    {loading ? (
      <div className="rounded-xl bg-white shadow-sm dark:border-default-200 dark:bg-content1 p-2">
        {[0, 1, 2].map((i) => (
          <div key={i} className={`flex items-center gap-3 px-3 py-3 ${i < 2 ? "border-b border-neutral-100 dark:border-default-100/40" : ""}`}>
            <Skeleton className="rounded-full w-8 h-8" />
            <div className="flex-1">
              <Skeleton className="h-4 w-40 mb-2" />
              <Skeleton className="h-3 w-56" />
            </div>
          </div>
        ))}
      </div>
    ) : (
      <Listbox selectedKeys={values} items={filteredUsers} label="Assigned to" selectionMode="multiple" topContent={topContent} variant="flat"  onSelectionChange={handleSelectionChange}
        classNames={{
          base: "rounded-xl bg-white shadow-sm dark:border-default-200 dark:bg-content1",
          list: "max-h-[500px] overflow-auto",
        }}>
        {(item) => (
          <ListboxItem key={item.id} textValue={item.fullname} className="hover:bg-neutral-100 dark:hover:bg-default-100">
            <div className="flex items-center gap-3">
              <Avatar size="sm" src={`https://api.dicebear.com/9.x/thumbs/svg?seed=${encodeURIComponent(item.fullname || String(item.id))}`}/>
              <div className="flex flex-col">
                <span className="text-sm font-medium text-neutral-800 dark:text-foreground capitalize">
                  {item.fullname}
                </span>
                <span className="text-xs text-neutral-600 dark:text-default-500">
                  {item.email_address}
                </span>
              </div>
            </div>
          </ListboxItem>
        )}
      </Listbox>
    )}

    <CreateMemberDrawer show={showCreate} onResp={onCreateMemberDrawerResp}/>
  </div>
);
};

export default InviteMembersComp;